package us.laantech.common.api

import com.russhwolf.settings.Settings
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.json.Json
import kotlin.coroutines.cancellation.CancellationException
import kotlin.time.Clock


/** Thrown by the HTTP interceptor when a request is 401 and the session cannot be
 *  refreshed. Subclasses CancellationException so it unwinds the caller's coroutine
 *  silently (no crash); navigation to login is driven by [KtorClient.authState]. */
class SessionExpiredException :
    CancellationException("Session expired — re-authentication required")

/**
 * Shared authenticated HTTP client for the LAAN API.
 *
 * Call [configure] once at app startup, then use [instance]. No engine is passed to
 * [HttpClient]: `ktor-client-engine-defaults` resolves the platform engine.
 */
class KtorClient private constructor(
    private val env: ApiEnv,
    private val deviceIdentifier: String?,
) {

    companion object {
        private const val TAG = "KtorClient"

        private const val KEY_ACCESS_TOKEN = "access_token"
        private const val KEY_ID_TOKEN = "id_token"
        private const val KEY_REFRESH_TOKEN = "refresh_token"
        private const val KEY_TOKEN_TYPE = "token_type"
        private const val KEY_EXPIRES_IN = "expires_in"
        private const val KEY_TOKEN_EXPIRY_AT = "token_expiry_at"
        private const val KEY_REFRESH_TOKEN_ISSUED_AT = "refresh_token_issued_at"

        private var config: Pair<ApiEnv, String?>? = null

        /**
         * Must run before the first access to [instance]. Idempotent; calling it again after
         * [instance] has been created has no effect.
         *
         * @param deviceIdentifier when non-null, sent as the `X-Device-Identifier` header on
         *   every API request (POS passes the Android ID; Admin/Manage pass nothing).
         */
        fun configure(env: ApiEnv, deviceIdentifier: String? = null) {
            config = env to deviceIdentifier
        }

        val instance: KtorClient by lazy {
            val (env, deviceId) = requireNotNull(config) {
                "KtorClient.configure() must be called before first use"
            }
            KtorClient(env, deviceId)
        }

        /** Convenience for callers that observe login state without holding the instance. */
        val authState: StateFlow<Boolean> get() = instance.authState
    }

    private val settings = Settings()
    private val refreshMutex = Mutex()

    private val _authFailedEvent = MutableSharedFlow<Unit>(extraBufferCapacity = 1)
    val authFailedEvent: SharedFlow<Unit> = _authFailedEvent.asSharedFlow()

    private var _accessToken: String? = settings.getStringOrNull(KEY_ACCESS_TOKEN)
    private var _idToken: String? = settings.getStringOrNull(KEY_ID_TOKEN)
    private var _refreshToken: String? = settings.getStringOrNull(KEY_REFRESH_TOKEN)
    private var _tokenType: String? = settings.getStringOrNull(KEY_TOKEN_TYPE)
    private var _expiresIn: Int? = settings.getIntOrNull(KEY_EXPIRES_IN)
    private var _tokenExpiryAt: Long? = settings.getLongOrNull(KEY_TOKEN_EXPIRY_AT)
    private var _refreshTokenIssuedAt: Long? = settings.getLongOrNull(KEY_REFRESH_TOKEN_ISSUED_AT)

    // Declared after the token fields so its initializer observes them (Kotlin
    // initializes properties in declaration order).
    private val _authState = MutableStateFlow(hasLocallyValidToken())

    /** Single source of truth for "is the user logged in". Set true by storeTokens()
     *  (login / refresh), false by clearTokens() (logout / auth failure). */
    val authState: StateFlow<Boolean> = _authState.asStateFlow()

    private val jsonConfig = Json {
        encodeDefaults = true
        ignoreUnknownKeys = true
        isLenient = true
        explicitNulls = true
        coerceInputValues = true
    }

    init {
        println("$TAG Initializing with apiBaseUrl: ${env.apiBaseUrl}, deviceIdentifier: $deviceIdentifier")
    }

    /** Talks to the Cognito token endpoint only; all calls use absolute URLs. */
    private val _authClient: HttpClient = HttpClient {
        install(ContentNegotiation) { json(jsonConfig) }
    }

    var client: HttpClient = HttpClient {
        defaultRequest {
            url { takeFrom(env.apiBaseUrl) }
            contentType(ContentType.Application.Json)
            deviceIdentifier?.let { header("X-Device-Identifier", it) }
        }

        install(ContentNegotiation) { json(jsonConfig) }

        install(HttpRequestRetry) {
            retryOnServerErrors(maxRetries = 3)
            exponentialDelay()
        }
    }.apply {
        // Intercept requests to handle 401 Unauthorized responses
        plugin(HttpSend).intercept { request ->
            // Re-read from Settings if in-memory token is null
            if (_accessToken == null) {
                _accessToken = settings.getStringOrNull(KEY_ACCESS_TOKEN)
                _tokenType = settings.getStringOrNull(KEY_TOKEN_TYPE)
            }

            // Proactively refresh if the access token is expired
            if (_accessToken != null && isTokenExpired()) {
                println("$TAG Access token expired, refreshing proactively...")
                refreshToken()
            }

            // Attach current access token
            _accessToken?.let {
                request.headers.remove(HttpHeaders.Authorization)
                request.headers.append(HttpHeaders.Authorization, "$_tokenType $it")
            }

            var response: HttpClientCall? = null

            try {
                response = execute(request)
            } catch (e: SendCountExceedException) {
                println("$TAG SendCountExceedException for request: ${request.url} - ${e.message}")
            }

            require(response != null) { "Response is null after request execution" }

            // On 401, attempt refresh and retry exactly once
            if (response.response.status == HttpStatusCode.Unauthorized) {
                println("$TAG Received 401 for ${request.url}, attempting token refresh...")

                val refreshed = if (isRefreshTokenExpired()) {
                    println("$TAG Refresh token expired locally, skipping refresh")
                    false
                } else {
                    refreshToken()
                }

                if (refreshed && _accessToken != null) {
                    println("$TAG Token refresh successful, retrying request")
                    request.headers.remove(HttpHeaders.Authorization)
                    request.headers.append(HttpHeaders.Authorization, "$_tokenType $_accessToken")

                    try {
                        response = execute(request)
                        println("$TAG Retry response status: ${response.response.status}")
                    } catch (e: Exception) {
                        println("$TAG Retry failed after token refresh: $e")
                    }
                }

                // Final gate: if refresh failed or retry still unauthorized, give up.
                if (!refreshed || response.response.status == HttpStatusCode.Unauthorized) {
                    println("$TAG Auth failed after refresh attempt, triggering logout")
                    clearTokens()
                    _authFailedEvent.tryEmit(Unit)
                    // Abort delivery of the 401 to the caller. Throwing a
                    // CancellationException unwinds the caller's coroutine silently (no
                    // crash / no .body() deserialization error); clearTokens() flipped
                    // authState to false, which drives the recompose to LoginView.
                    throw SessionExpiredException()
                }
            }

            response
        }
    }

    suspend fun exchangeCodeForTokens(
        authorizationCode: String,
        redirectUri: String = env.cognitoCallbackUrl,
    ): AuthResult {
        val response = _authClient.post(env.cognitoTokenEndpoint) {
            contentType(ContentType.Application.FormUrlEncoded)
            setBody(
                listOf(
                    "grant_type" to "authorization_code",
                    "code" to authorizationCode,
                    "client_id" to env.cognitoClientId,
                    "redirect_uri" to redirectUri,
                ).formUrlEncode()
            )
        }

        require(response.status == HttpStatusCode.OK) {
            "${response.status} Failed to exchange code for tokens: ${response.bodyAsText()}"
        }

        val authResult = response.body<OAuth2TokenResponse>().toAuthResult()
        storeTokens(authResult)
        printStoredTokens()
        return authResult
    }

    private fun storeTokens(authResult: AuthResult) {
        _accessToken = authResult.AccessToken
        _expiresIn = authResult.ExpiresIn
        _idToken = authResult.IdToken
        _tokenType = authResult.TokenType

        val expiryAt = Clock.System.now().epochSeconds + authResult.ExpiresIn
        _tokenExpiryAt = expiryAt

        settings.putString(KEY_ACCESS_TOKEN, authResult.AccessToken)
        settings.putString(KEY_ID_TOKEN, authResult.IdToken)
        settings.putString(KEY_TOKEN_TYPE, authResult.TokenType)
        settings.putInt(KEY_EXPIRES_IN, authResult.ExpiresIn)
        settings.putLong(KEY_TOKEN_EXPIRY_AT, expiryAt)

        // Cognito does not return a new RefreshToken on refresh flows, so only overwrite if present
        authResult.RefreshToken?.let {
            _refreshToken = it
            settings.putString(KEY_REFRESH_TOKEN, it)
            val issuedAt = Clock.System.now().epochSeconds
            _refreshTokenIssuedAt = issuedAt
            settings.putLong(KEY_REFRESH_TOKEN_ISSUED_AT, issuedAt)
        }

        _authState.value = true
    }

    /** Non-suspend, no-network check used for the initial authState value: logged in if
     *  the access token is unexpired OR the refresh token is still locally valid. */
    private fun hasLocallyValidToken(): Boolean {
        val now = Clock.System.now().epochSeconds
        val accessValid = _accessToken != null && now < (_tokenExpiryAt ?: 0) - 30
        val refreshValid = _refreshToken != null &&
                now < (_refreshTokenIssuedAt ?: 0) + env.refreshTokenValiditySeconds
        return accessValid || refreshValid
    }

    /** Suspend, refresh-aware. Returns true if a usable access token is (or becomes,
     *  via a silent refresh) available. Used at startup to decide the login screen. */
    suspend fun hasValidToken(): Boolean {
        if (_accessToken != null && !isTokenExpired()) return true

        val hasRefreshToken = (_refreshToken ?: settings.getStringOrNull(KEY_REFRESH_TOKEN)) != null
        if (hasRefreshToken && !isRefreshTokenExpired()) return refreshToken()

        return false
    }

    private fun isTokenExpired(): Boolean {
        val expiryAt = _tokenExpiryAt
            ?: settings.getLongOrNull(KEY_TOKEN_EXPIRY_AT)?.also { _tokenExpiryAt = it }
            ?: return true
        return Clock.System.now().epochSeconds >= (expiryAt - 30)
    }

    private fun isRefreshTokenExpired(): Boolean {
        val issuedAt = _refreshTokenIssuedAt
            ?: settings.getLongOrNull(KEY_REFRESH_TOKEN_ISSUED_AT)?.also { _refreshTokenIssuedAt = it }
            ?: return true
        return Clock.System.now().epochSeconds >= (issuedAt + env.refreshTokenValiditySeconds)
    }

    fun clearTokens() {
        _accessToken = null
        _idToken = null
        _refreshToken = null
        _tokenType = null
        _expiresIn = null
        _tokenExpiryAt = null
        _refreshTokenIssuedAt = null

        settings.remove(KEY_ACCESS_TOKEN)
        settings.remove(KEY_ID_TOKEN)
        settings.remove(KEY_REFRESH_TOKEN)
        settings.remove(KEY_TOKEN_TYPE)
        settings.remove(KEY_EXPIRES_IN)
        settings.remove(KEY_TOKEN_EXPIRY_AT)
        settings.remove(KEY_REFRESH_TOKEN_ISSUED_AT)

        _authState.value = false
    }

    fun printStoredTokens() {
        println("$TAG === Stored Tokens ===")
        println("$TAG access_token:  ${settings.getStringOrNull(KEY_ACCESS_TOKEN)?.take(20)}...")
        println("$TAG id_token:      ${settings.getStringOrNull(KEY_ID_TOKEN)?.take(20)}...")
        println("$TAG refresh_token: ${settings.getStringOrNull(KEY_REFRESH_TOKEN)?.take(20)}...")
        println("$TAG token_type:    ${settings.getStringOrNull(KEY_TOKEN_TYPE)}")
        println("$TAG expires_in:    ${settings.getIntOrNull(KEY_EXPIRES_IN)}")
        println("$TAG device_identifier: $deviceIdentifier")
        println("$TAG === In Memory Tokens ===")
        println("$TAG accessToken: ${_accessToken?.take(20)}")
        println("$TAG tokenType:   $_tokenType")
    }

    /**
     * Refresh via the hosted-UI OAuth2 token endpoint (grant_type=refresh_token),
     * matching exchangeCodeForTokens(). Cognito refresh responses omit a new
     * refresh_token, so storeTokens() keeps the existing one.
     */
    private suspend fun refreshToken(): Boolean = refreshMutex.withLock {
        println("$TAG Refreshing token...")

        // Fall back to Settings if in-memory refresh token is null
        val refreshToken = _refreshToken
            ?: settings.getStringOrNull(KEY_REFRESH_TOKEN)?.also { _refreshToken = it }

        if (refreshToken == null) {
            println("$TAG No refresh token available (checked memory and Settings)")
            return@withLock false
        }

        try {
            val response = _authClient.post(env.cognitoTokenEndpoint) {
                contentType(ContentType.Application.FormUrlEncoded)
                setBody(
                    listOf(
                        "grant_type" to "refresh_token",
                        "client_id" to env.cognitoClientId,
                        "refresh_token" to refreshToken,
                    ).formUrlEncode()
                )
            }

            if (response.status != HttpStatusCode.OK) {
                println("$TAG Refresh token failed: ${response.status} - ${response.bodyAsText()}")
                return@withLock false
            }

            storeTokens(response.body<OAuth2TokenResponse>().toAuthResult())
            println("$TAG Token refreshed successfully")
            true
        } catch (e: Exception) {
            println("$TAG Exception during token refresh: $e")
            false
        }
    }
}
